import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, notification } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { useNotifications } from '../../contexts/NotificationContext';
import { notificationTarget } from './notificationTarget';
import css from './NotifierToast.module.scss';

/**
 * Surfaces the moment new matches land.
 *
 * Since the per-user daily cap (2026-07-27) this fires for two different
 * events, and both deserve the same treatment:
 *
 *  - first run, right after a search is created (carries a search id)
 *  - the 05:00 UTC sweep, once a day for the whole account (searchId null)
 *
 * The sweep is now the ONLY recurring reveal — recrawls no longer surface
 * anything when they finish — so without this the single most important
 * recurring moment in the product happens with no signal at all.
 */
const OpportunitiesReadyNotifier: React.FC = () => {
  const { subscribe } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  // Both of these are read through refs so the subscription below can have a
  // genuinely empty-ish dep list. It matters: NotificationContext clears a
  // subscriber's pending 500ms debounce timer on unsubscribe, so any effect
  // that re-runs on navigation would silently swallow an event that landed
  // mid-navigation.
  //
  // `pathname` obviously changes. `navigate` does too — despite the usual
  // "useNavigate is stable" advice, its identity DOES change on location
  // change in the react-router 6.x this app pins, which
  // OpportunitiesReadyNotifier.test.tsx pins down.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    return subscribe(['opportunities_ready'], (event) => {
      // Telling someone to go where they already are is noise — and the page
      // refetches itself on this same event, so they will see the new rows.
      if (pathnameRef.current.startsWith('/opportunities')) return;

      const target = notificationTarget(event);
      const key = `opportunities-ready-${event.id}`;

      notification.info({
        key,
        message: event.title,
        description: event.message,
        placement: 'topRight',
        duration: 8,
        icon: <ThunderboltOutlined style={{ color: '#207551' }} />,
        btn: target ? (
          <Button
            type="primary"
            size="small"
            className={css.reviewButton}
            onClick={() => {
              notification.destroy(key);
              navigateRef.current(target);
            }}
          >
            Review matches
          </Button>
        ) : undefined,
      });
    });
    // Subscribe ONCE — everything mutable is behind a ref.
  }, [subscribe]);

  return null;
};

export default OpportunitiesReadyNotifier;
